import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";

const roles = ["Broker", "Investor", "Operator", "Acquisitions", "Lender", "Other"];
const sources = ["Referral", "LinkedIn", "Conference/Event", "Search", "Other"];

export default function Waitlist() {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("");
  const [heardFrom, setHeardFrom] = useState("");
  const [referralSource, setReferralSource] = useState("");
  const [dealTypeNotes, setDealTypeNotes] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!firstName || !lastName || !email || !company.trim() || !role || !heardFrom) {
      setError("Please fill in all required fields.");
      return;
    }

    setLoading(true);

    const { error: insertError } = await supabase.from("waitlist_users").insert({
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      email: email.toLowerCase().trim(),
      company: company.trim(),
      role,
      heard_from: heardFrom,
      referral_source: heardFrom === "Referral" ? referralSource.trim() || null : null,
      deal_type_notes: dealTypeNotes.trim() || null,
    });

    setLoading(false);
    if (insertError) {
      if (insertError.code === "23505") {
        setError("You are already on the waitlist.");
      } else {
        setError("Something went wrong. Please try again.");
      }
      return;
    }

    // Confirmation email. Fire-and-forget — the signup itself already
    // succeeded and must not look failed over email trouble — but never
    // silent: a swallowed catch here is how a 401 on every single
    // confirmation went unnoticed since launch. The function derives the
    // name from the waitlist row, so only type and email travel.
    try {
      const { error: emailError } = await supabase.functions.invoke("waitlist-email", {
        body: { type: "confirmation", email: email.toLowerCase().trim() },
      });
      if (emailError) console.error("waitlist confirmation email failed:", emailError);
    } catch (err) {
      console.error("waitlist confirmation email failed:", err);
    }

    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full text-center space-y-6"
        >
          <CheckCircle2 className="w-16 h-16 text-accent mx-auto" />
          <h1 className="font-display text-3xl font-bold text-foreground">You're on the list.</h1>
          <p className="text-muted-foreground">
            We review access requests regularly. If approved, you'll receive an invite link by email.
          </p>
          <Button variant="outline" onClick={() => navigate("/")}>Return Home</Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-lg w-full space-y-8"
      >
        <div className="text-center space-y-3">
          <div className="inline-block bg-accent/10 text-accent text-xs font-display font-semibold px-3 py-1 rounded-full">
            Private Beta – Access by Approval
          </div>
          <h1 className="font-display text-3xl font-bold text-foreground">Request Early Access</h1>
          <p className="text-muted-foreground">
            Brickshare is currently in private beta. Submit your request and we will notify you once your access is approved.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">First Name *</label>
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Last Name *</label>
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last name" />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">Email *</label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">Company / Firm *</label>
            <Input
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Summit Capital Partners"
            />
            <p className="text-xs text-muted-foreground">
              This becomes the name of your Brickshare workspace, where your deals and your team live.
            </p>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">Role *</label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger><SelectValue placeholder="Select your role" /></SelectTrigger>
              <SelectContent>
                {roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">How did you hear about Brickshare? *</label>
            <Select value={heardFrom} onValueChange={setHeardFrom}>
              <SelectTrigger><SelectValue placeholder="Select source" /></SelectTrigger>
              <SelectContent>
                {sources.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {heardFrom === "Referral" && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Referral name or source</label>
              <Input value={referralSource} onChange={(e) => setReferralSource(e.target.value)} placeholder="Who referred you?" />
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">What type of deals do you analyze?</label>
            <Textarea
              value={dealTypeNotes}
              onChange={(e) => setDealTypeNotes(e.target.value)}
              placeholder="Multifamily acquisitions, value-add deals, etc."
              rows={3}
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <Button type="submit" disabled={loading} className="w-full bg-accent text-accent-foreground hover:bg-accent/90 h-12 font-display text-base gap-2">
            {loading ? "Submitting…" : "Join Waitlist"}
            {!loading && <ArrowRight className="w-4 h-4" />}
          </Button>

          {/* The form collects a name, an email, a company and free-text notes,
              and until now said nothing about what happens to them. This is the
              moment the data is handed over, so it is the moment the policy has
              to be reachable — not only from the landing page footer. */}
          <p className="text-xs text-center text-muted-foreground leading-relaxed">
            By submitting, you agree to our{" "}
            <a href="/terms" className="underline hover:text-foreground">Terms of Service</a>
            {" "}and{" "}
            <a href="/privacy" className="underline hover:text-foreground">Privacy Policy</a>.
          </p>
        </form>

        <p className="text-xs text-center text-muted-foreground">
          Access is limited while we onboard early users.
        </p>
      </motion.div>
    </div>
  );
}
